import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X, Plus, AlertTriangle, Mail } from 'lucide-react';

const STATUS_COLORS = {
  current:       { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  expiring_soon: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  expired:       { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  missing:       { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' }
};

const STATUS_LABELS = {
  current: 'Current',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  missing: 'Missing'
};

// API returns YYYY-MM-DD strings. Reformat to MM/DD/YYYY without new Date()
// to avoid a UTC-parse off-by-one.
const formatDate = (str) => {
  if (!str) return '—';
  const parts = str.split('-');
  if (parts.length !== 3) return str;
  const [y, m, d] = parts;
  return `${m}/${d}/${y}`;
};

// Local YYYY-MM-DD for today (no UTC shift).
const todayYMD = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// Preview-only: add N months to a YYYY-MM-DD string, clamping to end-of-month
// the way MySQL DATE_ADD does. The server is the source of truth on save.
const addMonths = (ymd, months) => {
  const parts = ymd.split('-').map(Number);
  if (parts.length !== 3 || months == null) return null;
  const [y, m, d] = parts;
  const target = new Date(y, m - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  return target;
};

const fieldWrap = { marginBottom: '1rem' };
const fieldLabel = { display: 'block', marginBottom: '0.375rem', fontSize: '0.875rem', fontWeight: '500', color: '#374151' };
const fieldInput = { width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' };

// Reusable modal chrome — defined at module level so form inputs keep focus
// across the parent's re-renders.
const ModalShell = ({ title, onClose, children, footer, maxWidth = '500px' }) => (
  <div
    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
    onClick={onClose}
  >
    <div
      style={{ background: 'white', borderRadius: '0.75rem', maxWidth, width: '100%', maxHeight: '90vh', overflow: 'auto', display: 'flex', flexDirection: 'column' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>{title}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', lineHeight: 0 }}>
          <X size={22} color="#6b7280" />
        </button>
      </div>
      <div style={{ padding: '1.5rem' }}>{children}</div>
      <div style={{ padding: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>{footer}</div>
    </div>
  </div>
);

// "Staff Conversations" section of Add / Edit Staff. The checkbox decides
// whether this person appears in Staff Conversations at all: cert_staff also
// holds people who are cert-tracked only. Unchecked, all six fields
// (including preceptor_name) are saved as NULL. Cycle labels and the default department come from
// /api/cert/lookups, so the server stays the single source for both.
const StaffConversationFields = ({ formState, setField, scLookups }) => {
  const cycles = Object.entries(scLookups.quarterly_cycles || {});
  return (
    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginBottom: '1rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!formState.sc_include}
          onChange={(e) => {
            setField('sc_include', e.target.checked);
            if (e.target.checked && !formState.department) setField('department', scLookups.default_department || '');
          }}
        />
        Include in Staff Conversations
      </label>
      {!formState.sc_include && (
        <div style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: '#6b7280' }}>
          Cert tracking only — this person will not appear in Staff Conversations.
        </div>
      )}
      {formState.sc_include && (
        <div style={{ marginTop: '0.875rem' }}>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Hire Date</label>
            <input type="date" style={fieldInput} max={todayYMD()} value={formState.hire_date} onChange={(e) => setField('hire_date', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div style={fieldWrap}>
              <label style={fieldLabel}>Job Title (optional)</label>
              <input type="text" style={fieldInput} maxLength={50} placeholder="RN, LPN, C.N.A." value={formState.job_title} onChange={(e) => setField('job_title', e.target.value)} />
            </div>
            <div style={fieldWrap}>
              <label style={fieldLabel}>Shift (optional)</label>
              <input type="text" style={fieldInput} maxLength={50} placeholder="7a-3p, 11p-7a, PRN" value={formState.shift} onChange={(e) => setField('shift', e.target.value)} />
            </div>
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Quarterly Conversations</label>
            <select style={fieldInput} value={formState.quarterly_cycle} onChange={(e) => setField('quarterly_cycle', e.target.value)}>
              <option value="">None (no quarterlies required)</option>
              {cycles.map(([key, c]) => <option key={key} value={key}>{c.label}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Department</label>
            <input type="text" style={fieldInput} maxLength={100} value={formState.department} onChange={(e) => setField('department', e.target.value)} />
          </div>
          <div style={{ ...fieldWrap, marginBottom: 0 }}>
            <label style={fieldLabel}>Preceptor Name (optional)</label>
            <input type="text" style={fieldInput} maxLength={100} placeholder="Orientation mentor" value={formState.preceptor_name} onChange={(e) => setField('preceptor_name', e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
};

// The Staff Conversations columns as sent to POST / PUT /api/cert/staff. Unchecked sends
// explicit NULLs, which is how Edit Staff takes someone out again.
const staffConversationPayload = (f) => (f.sc_include
  ? {
      hire_date: f.hire_date || null,
      job_title: f.job_title.trim() || null,
      shift: f.shift.trim() || null,
      quarterly_cycle: f.quarterly_cycle || null,
      department: f.department.trim() || null,
      preceptor_name: f.preceptor_name.trim() || null
    }
  : { hire_date: null, job_title: null, shift: null, quarterly_cycle: null, department: null, preceptor_name: null });

const CertTracking = () => {
  const [stats, setStats] = useState(null);
  const [roster, setRoster] = useState([]);
  const [lookups, setLookups] = useState({ cert_types: [], cert_staff_roles: [], staff_conversations: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [activeModal, setActiveModal] = useState(null); // null | 'logCert' | 'addStaff' | 'editStaff' | 'staffDetail'
  const [modalContext, setModalContext] = useState(null); // staff row that triggered the modal
  const [formState, setFormState] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [detailData, setDetailData] = useState(null); // { staff, history } for the staffDetail modal
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [digestSending, setDigestSending] = useState(false);
  const [digestMessage, setDigestMessage] = useState(null); // { type: 'success'|'error', text } | null

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, staffRes, lookupsRes] = await Promise.all([
        fetch('/api/cert/dashboard-stats'),
        fetch('/api/cert/staff'),
        fetch('/api/cert/lookups')
      ]);

      if (!statsRes.ok) throw new Error(`dashboard-stats failed (status ${statsRes.status})`);
      if (!staffRes.ok) throw new Error(`staff roster failed (status ${staffRes.status})`);
      if (!lookupsRes.ok) throw new Error(`lookups failed (status ${lookupsRes.status})`);

      const statsJson = await statsRes.json();
      const staffJson = await staffRes.json();
      const lookupsJson = await lookupsRes.json();

      setStats(statsJson);
      setRoster(Array.isArray(staffJson) ? staffJson : []);
      setLookups({
        cert_types: Array.isArray(lookupsJson?.cert_types) ? lookupsJson.cert_types : [],
        cert_staff_roles: Array.isArray(lookupsJson?.cert_staff_roles) ? lookupsJson.cert_staff_roles : [],
        staff_conversations: lookupsJson?.staff_conversations || {}
      });
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err.message || 'Failed to load cert data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeModal = () => {
    setActiveModal(null);
    setModalContext(null);
    setFormState({});
    setSaveError(null);
    setSaving(false);
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const fetchStaffDetail = async (staffId) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await fetch(`/api/cert/staff/${staffId}/history`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Request failed (status ${res.status})`);
      }
      const json = await res.json();
      setDetailData(json);
    } catch (err) {
      setDetailError(err.message || 'Failed to load staff history');
    } finally {
      setDetailLoading(false);
    }
  };

  const openModal = (type, staff = null) => {
    setSaveError(null);
    setSaving(false);
    setModalContext(staff);
    if (type === 'logCert') {
      setFormState({ cert_type_id: '', date_earned: todayYMD(), notes: '' });
    } else if (type === 'addStaff') {
      // Opt-in on Add: most new cert_staff rows are Kasey's unit, but not all.
      setFormState({
        first_name: '', last_name: '', cert_staff_role_id: '', phone: '', notes: '',
        sc_include: false, hire_date: '', job_title: '', shift: '', quarterly_cycle: '',
        department: lookups.staff_conversations.default_department || '', preceptor_name: ''
      });
    } else if (type === 'editStaff') {
      setFormState({
        first_name: staff.first_name || '',
        last_name: staff.last_name || '',
        cert_staff_role_id: staff.cert_staff_role_id != null ? String(staff.cert_staff_role_id) : '',
        phone: staff.phone || '',
        notes: staff.notes || '',
        active: staff.active === undefined || staff.active === null ? true : !!staff.active,
        // hire_date is what puts someone in Staff Conversations, so it drives the checkbox.
        sc_include: !!staff.hire_date,
        hire_date: staff.hire_date || '',
        job_title: staff.job_title || '',
        shift: staff.shift || '',
        quarterly_cycle: staff.quarterly_cycle || '',
        department: staff.department || lookups.staff_conversations.default_department || '',
        preceptor_name: staff.preceptor_name || ''
      });
    } else if (type === 'staffDetail') {
      setDetailData(null);
      setDetailError(null);
      fetchStaffDetail(staff.id);
    }
    setActiveModal(type);
  };

  const setField = (name, value) => setFormState(prev => ({ ...prev, [name]: value }));

  const canSave = () => {
    if (activeModal === 'logCert') {
      return !!formState.cert_type_id && !!formState.date_earned;
    }
    if (activeModal === 'addStaff' || activeModal === 'editStaff') {
      return !!(formState.first_name && formState.first_name.trim()) &&
             !!(formState.last_name && formState.last_name.trim()) &&
             !!formState.cert_staff_role_id &&
             (!formState.sc_include || !!formState.hire_date);
    }
    return false;
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const headers = { 'Content-Type': 'application/json' };

      let res;
      if (activeModal === 'logCert') {
        res = await fetch('/api/cert/certifications', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            cert_staff_id: modalContext.id,
            cert_type_id: formState.cert_type_id,
            date_earned: formState.date_earned,
            notes: formState.notes ? formState.notes.trim() : null
          })
        });
      } else if (activeModal === 'addStaff') {
        res = await fetch('/api/cert/staff', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            first_name: formState.first_name,
            last_name: formState.last_name,
            cert_staff_role_id: formState.cert_staff_role_id,
            phone: formState.phone ? formState.phone.trim() : null,
            notes: formState.notes ? formState.notes.trim() : null,
            ...staffConversationPayload(formState)
          })
        });
      } else if (activeModal === 'editStaff') {
        res = await fetch(`/api/cert/staff/${modalContext.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            first_name: formState.first_name,
            last_name: formState.last_name,
            cert_staff_role_id: formState.cert_staff_role_id,
            phone: formState.phone ? formState.phone.trim() : null,
            notes: formState.notes ? formState.notes.trim() : null,
            active: formState.active ? 1 : 0,
            ...staffConversationPayload(formState)
          })
        });
      }

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.error || `Request failed (status ${res.status})`);
      }

      closeModal();
      await fetchData();
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendDigest = async () => {
    setDigestSending(true);
    setDigestMessage(null);
    try {
      const res = await fetch('/api/cert/send-digest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.error || `Request failed (status ${res.status})`);
      }
      setDigestMessage({ type: 'success', text: json.message || 'Digest sent' });
    } catch (err) {
      setDigestMessage({ type: 'error', text: err.message || 'Failed to send digest' });
    } finally {
      setDigestSending(false);
      setTimeout(() => setDigestMessage(null), 6000);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '3rem' }}>
        <div style={{ width: '60px', height: '60px', border: '4px solid #f3f4f6', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
        <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>Loading cert data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '2rem', color: '#991b1b' }}>
        <p style={{ fontWeight: '700' }}>Error</p>
        <p>{error}</p>
        <button onClick={fetchData} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600' }}>
          Try Again
        </button>
      </div>
    );
  }

  // Cert-type columns: union of abbreviations across the roster, sorted.
  const certTypeColumns = Array.from(
    new Set(roster.flatMap(s => s.certs.map(c => c.abbreviation)))
  ).sort();

  const pct = stats ? stats.compliance_percentage : 0;
  const heroColor = pct >= 90 ? '#15803d' : pct >= 75 ? '#92400e' : '#991b1b';

  // "Days to Soonest Expiry" cell content for one staff member.
  const soonestCell = (certs) => {
    const expired = certs.filter(c => c.status === 'expired');
    if (expired.length) return { text: 'Expired', color: '#991b1b' };
    const present = certs.filter(c => c.status === 'current' || c.status === 'expiring_soon');
    if (present.length) {
      const min = Math.min(...present.map(c => c.days_until_expires));
      return { text: `${min} days`, color: min <= 60 ? '#92400e' : '#374151' };
    }
    if (certs.some(c => c.status === 'missing')) return { text: 'Missing', color: '#374151' };
    return { text: '—', color: '#9ca3af' };
  };

  const pill = (status) => {
    const c = STATUS_COLORS[status] || STATUS_COLORS.missing;
    return (
      <span style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', background: c.bg, color: c.text }}>
        {STATUS_LABELS[status] || status}
      </span>
    );
  };

  const thStyle = { padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' };
  const detailTdStyle = { padding: '0.5rem 0.75rem', color: '#374151', verticalAlign: 'top' };

  // Live "Expires on" preview for the Log Completion modal.
  const logCertPreview = () => {
    if (activeModal !== 'logCert') return null;
    const ct = lookups.cert_types.find(t => String(t.id) === String(formState.cert_type_id));
    if (!ct || !formState.date_earned || !/^\d{4}-\d{2}-\d{2}$/.test(formState.date_earned)) return null;
    const expires = addMonths(formState.date_earned, ct.validity_months);
    if (!expires) return null;
    const mm = String(expires.getMonth() + 1).padStart(2, '0');
    const dd = String(expires.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${expires.getFullYear()}`;
  };

  const modalFooter = (
    <>
      <button
        onClick={closeModal}
        disabled={saving}
        style={{ padding: '0.5rem 1rem', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        disabled={saving || !canSave()}
        style={{ padding: '0.5rem 1rem', background: (saving || !canSave()) ? '#93c5fd' : '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: (saving || !canSave()) ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: '600' }}
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </>
  );

  const errorBlock = saveError
    ? <div style={{ marginTop: '0.5rem', color: '#991b1b', fontSize: '0.875rem' }}>{saveError}</div>
    : null;

  const renderModal = () => {
    if (!activeModal) return null;

    if (activeModal === 'logCert') {
      const preview = logCertPreview();
      return (
        <ModalShell
          title={`Log Completion for ${modalContext.first_name} ${modalContext.last_name}`}
          onClose={closeModal}
          footer={modalFooter}
        >
          <div style={fieldWrap}>
            <label style={fieldLabel}>Cert Type</label>
            <select style={fieldInput} value={formState.cert_type_id} onChange={(e) => setField('cert_type_id', e.target.value)}>
              <option value="">Select a cert type...</option>
              {lookups.cert_types.map(ct => (
                <option key={ct.id} value={ct.id}>{ct.abbreviation} — {ct.name}</option>
              ))}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Date Earned</label>
            <input type="date" style={fieldInput} value={formState.date_earned} onChange={(e) => setField('date_earned', e.target.value)} />
            {preview && (
              <div style={{ marginTop: '0.375rem', fontSize: '0.8125rem', color: '#15803d', fontWeight: '500' }}>
                Expires on {preview}
              </div>
            )}
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Notes (optional)</label>
            <textarea style={{ ...fieldInput, minHeight: '4rem', resize: 'vertical' }} value={formState.notes} onChange={(e) => setField('notes', e.target.value)} />
          </div>
          {errorBlock}
        </ModalShell>
      );
    }

    if (activeModal === 'addStaff') {
      return (
        <ModalShell title="Add New Staff Member" onClose={closeModal} footer={modalFooter}>
          <div style={fieldWrap}>
            <label style={fieldLabel}>First Name</label>
            <input type="text" style={fieldInput} value={formState.first_name} onChange={(e) => setField('first_name', e.target.value)} />
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Last Name</label>
            <input type="text" style={fieldInput} value={formState.last_name} onChange={(e) => setField('last_name', e.target.value)} />
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Role</label>
            <select style={fieldInput} value={formState.cert_staff_role_id} onChange={(e) => setField('cert_staff_role_id', e.target.value)}>
              <option value="">Select a role...</option>
              {lookups.cert_staff_roles.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Phone (optional)</label>
            <input type="text" style={fieldInput} placeholder="555-555-5555" value={formState.phone} onChange={(e) => setField('phone', e.target.value)} />
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Notes (optional)</label>
            <textarea style={{ ...fieldInput, minHeight: '4rem', resize: 'vertical' }} value={formState.notes} onChange={(e) => setField('notes', e.target.value)} />
          </div>
          <StaffConversationFields formState={formState} setField={setField} scLookups={lookups.staff_conversations} />
          {errorBlock}
        </ModalShell>
      );
    }

    if (activeModal === 'editStaff') {
      return (
        <ModalShell
          title={`Edit ${modalContext.first_name} ${modalContext.last_name}`}
          onClose={closeModal}
          footer={modalFooter}
        >
          <div style={fieldWrap}>
            <label style={fieldLabel}>First Name</label>
            <input type="text" style={fieldInput} value={formState.first_name} onChange={(e) => setField('first_name', e.target.value)} />
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Last Name</label>
            <input type="text" style={fieldInput} value={formState.last_name} onChange={(e) => setField('last_name', e.target.value)} />
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Role</label>
            <select style={fieldInput} value={formState.cert_staff_role_id} onChange={(e) => setField('cert_staff_role_id', e.target.value)}>
              <option value="">Select a role...</option>
              {lookups.cert_staff_roles.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Phone</label>
            <input type="text" style={fieldInput} placeholder="555-555-5555" value={formState.phone} onChange={(e) => setField('phone', e.target.value)} />
          </div>
          <div style={fieldWrap}>
            <label style={fieldLabel}>Notes</label>
            <textarea style={{ ...fieldInput, minHeight: '4rem', resize: 'vertical' }} value={formState.notes} onChange={(e) => setField('notes', e.target.value)} />
          </div>
          <StaffConversationFields formState={formState} setField={setField} scLookups={lookups.staff_conversations} />
          <div style={{ marginBottom: '0.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!formState.active} onChange={(e) => setField('active', e.target.checked)} />
              Active
            </label>
            {!formState.active && (
              <div style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: '#92400e' }}>
                Inactive staff are hidden from the dashboard. Their cert history is preserved.
              </div>
            )}
          </div>
          {errorBlock}
        </ModalShell>
      );
    }

    if (activeModal === 'staffDetail') {
      const detailFooter = (
        <>
          <button
            onClick={() => openModal('logCert', modalContext)}
            style={{ padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' }}
          >
            Add Cert
          </button>
          <button
            onClick={closeModal}
            style={{ padding: '0.5rem 1rem', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
          >
            Close
          </button>
        </>
      );

      return (
        <ModalShell
          title={`${modalContext.first_name} ${modalContext.last_name}`}
          onClose={closeModal}
          footer={detailFooter}
          maxWidth="800px"
        >
          {detailLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
              <div style={{ width: '40px', height: '40px', border: '4px solid #f3f4f6', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '0.75rem' }} />
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading history...</p>
            </div>
          )}

          {!detailLoading && detailError && (
            <div style={{ color: '#991b1b', fontSize: '0.875rem' }}>
              <p style={{ fontWeight: '700' }}>Error</p>
              <p>{detailError}</p>
              <button
                onClick={() => fetchStaffDetail(modalContext.id)}
                style={{ marginTop: '0.5rem', padding: '0.375rem 0.75rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: '600' }}
              >
                Try Again
              </button>
            </div>
          )}

          {!detailLoading && !detailError && detailData && (
            <>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.25rem' }}>
                {detailData.staff.role_name || 'No role'} · {detailData.staff.phone || 'No phone on file'}
              </div>

              <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                Current Standing
              </div>
              {detailData.staff.certs.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1.5rem' }}>No required certs for this role.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  {detailData.staff.certs.map(c => (
                    <div key={c.abbreviation} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: '#f9fafb', borderRadius: '0.375rem' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>{c.abbreviation}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                          {c.date_expires ? `Expires ${formatDate(c.date_expires)}` : '—'}
                        </span>
                        {pill(c.status)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                Cert History
              </div>
              {detailData.history.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No cert history yet.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={thStyle}>Cert Type</th>
                        <th style={thStyle}>Date Earned</th>
                        <th style={thStyle}>Date Expires</th>
                        <th style={thStyle}>Lapsed?</th>
                        <th style={thStyle}>Notes</th>
                        <th style={thStyle}>Recorded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailData.history.map(h => (
                        <tr key={h.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={detailTdStyle}>{h.abbreviation}</td>
                          <td style={detailTdStyle}>{formatDate(h.date_earned)}</td>
                          <td style={detailTdStyle}>{formatDate(h.date_expires)}</td>
                          <td style={detailTdStyle}>
                            {h.lapsed_before ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#991b1b', fontWeight: '600' }}>
                                <AlertTriangle size={14} /> Lapsed
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td style={detailTdStyle}>{h.notes || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                          <td style={detailTdStyle}>{formatDate(h.logged_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </ModalShell>
      );
    }

    return null;
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1600px', margin: '0 auto' }}>
      {renderModal()}

      {/* Header */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
            Cert Tracking Dashboard
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Last refreshed: {lastRefreshed ? lastRefreshed.toLocaleString() : '—'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={() => openModal('addStaff')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>
            <Plus size={16} />
            Add Staff
          </button>
          <button onClick={fetchData} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1rem', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={sendDigest}
            disabled={digestSending}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1rem', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: digestSending ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: '500', opacity: digestSending ? 0.6 : 1 }}
          >
            <Mail size={16} />
            {digestSending ? 'Sending...' : 'Send Digest'}
          </button>
        </div>
      </div>

      {digestMessage && (
        <div style={{
          padding: '0.75rem 1rem',
          background: digestMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: digestMessage.type === 'success' ? '#15803d' : '#991b1b',
          border: `1px solid ${digestMessage.type === 'success' ? '#86efac' : '#fca5a5'}`,
          borderRadius: '0.375rem',
          marginBottom: '1rem',
          fontSize: '0.875rem',
          fontWeight: '500'
        }}>
          {digestMessage.type === 'success' ? '✓ ' : ''}{digestMessage.text}
        </div>
      )}

      {/* Hero compliance number */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', marginBottom: '1.5rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3.5rem', fontWeight: '800', lineHeight: '1', color: heroColor }}>
          {pct}% Compliant
        </div>
        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
          {stats ? stats.fully_current : 0} of {stats ? stats.total_staff : 0} staff fully current
        </div>
      </div>

      {/* Four overview cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        {[
          { label: 'Total Staff', value: stats ? stats.total_staff : 0, color: '#1f2937' },
          { label: 'Fully Current', value: stats ? stats.fully_current : 0, color: '#15803d' },
          { label: 'Expired Certs', value: stats ? stats.staff_with_expired : 0, color: '#991b1b' },
          { label: 'Expiring Soon', value: stats ? stats.staff_expiring_soon : 0, color: '#92400e' }
        ].map(card => (
          <div key={card.label} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
              {card.label}
            </div>
            <div style={{ fontSize: '1.875rem', fontWeight: '700', color: card.color }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '1.5rem' }}>
        Note: counts can overlap — "Fully Current" includes anyone with no expired and no missing certs, so a staff member with a cert expiring soon is counted in both buckets.
      </p>

      {/* Staff roster table */}
      {roster.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '3rem', textAlign: 'center', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <p style={{ color: '#6b7280', fontSize: '1rem' }}>No staff found.</p>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={thStyle}>Staff Name</th>
                <th style={thStyle}>Role</th>
                {certTypeColumns.map(abbr => (
                  <th key={abbr} style={thStyle}>{abbr}</th>
                ))}
                <th style={thStyle}>Days to Soonest Expiry</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roster.map(staff => {
                const certByAbbr = {};
                staff.certs.forEach(c => { certByAbbr[c.abbreviation] = c; });
                const soonest = soonestCell(staff.certs);
                return (
                  <tr key={staff.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <button
                        onClick={() => openModal('staffDetail', staff)}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', fontWeight: '500', fontSize: '0.875rem', cursor: 'pointer', textAlign: 'left' }}
                      >
                        {staff.first_name} {staff.last_name}
                      </button>
                    </td>
                    <td style={{ padding: '0.75rem', color: '#374151' }}>
                      {staff.role_name || <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    {certTypeColumns.map(abbr => {
                      const cert = certByAbbr[abbr];
                      if (!cert) {
                        return <td key={abbr} style={{ padding: '0.75rem', color: '#9ca3af' }}>—</td>;
                      }
                      return (
                        <td key={abbr} style={{ padding: '0.75rem' }}>
                          <div style={{ marginBottom: '0.25rem', color: '#374151' }}>{formatDate(cert.date_expires)}</div>
                          {pill(cert.status)}
                        </td>
                      );
                    })}
                    <td style={{ padding: '0.75rem', fontWeight: '600', color: soonest.color }}>
                      {soonest.text}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.375rem' }}>
                        <button
                          onClick={() => openModal('logCert', staff)}
                          style={{ padding: '0.375rem 0.625rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '500', whiteSpace: 'nowrap' }}
                        >
                          Add Cert
                        </button>
                        <button
                          onClick={() => openModal('editStaff', staff)}
                          style={{ padding: '0.375rem 0.625rem', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '500' }}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CertTracking;
